import React, { useState } from 'react';
import { Eye, EyeOff, ScanEye, Info, X, User, Bike, Truck, ArrowLeft, ArrowRight } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · O4 · Angles morts
// Œuvre pédagogique interactive — vue de dessus 3/4 d'une voiture avec :
//  · Rétroviseurs (int, gauche, droit) avec cônes de vision (cyan)
//  · 5 zones aveugles cliquables (rouge, révélables par toggle)
//  · Piéton / vélo / camion posés dans les zones — draggables
//  · Panneau latéral qui s'ouvre au clic sur une zone : nom, danger, geste correct
//  · Toggle "Contrôle-épaule" : montre l'arc de rotation de la tête (vert)
// ══════════════════════════════════════════════════════════════════════════

const ZONES = [
  {
    id: 'avant',
    label: 'Angle mort avant',
    numero: 1,
    // Zone hexagonale devant le capot
    polygon: '340,180 460,180 500,240 400,255 300,240',
    color: '#ef4444',
    danger: 'Enfant / piéton petit',
    icon: 'user',
    iconPos: { x: 400, y: 220 },
    explication: "Devant le capot, sur environ 1,2 m, un enfant en bas âge ou une personne accroupie n'est PAS visible depuis le poste de conduite.",
    geste: "Faire le tour du véhicule avant tout démarrage en zone piétonne. Vérification systématique avant chaque redémarrage dans un parking.",
    stat: "En France, plus de 15 enfants sont écrasés chaque année dans l'allée de leur propre maison par un proche qui recule sans les voir.",
  },
  {
    id: 'lateral-g',
    label: 'Angle mort latéral GAUCHE',
    numero: 2,
    // Zone trapèze à gauche arrière
    polygon: '210,420 340,420 340,600 190,600',
    color: '#dc2626',
    danger: 'Deux-roues, cycliste',
    icon: 'bike',
    iconPos: { x: 265, y: 510 },
    explication: "Zone où un scooter, un vélo ou une petite voiture disparaît DES DEUX rétroviseurs (intérieur et extérieur gauche). Le véhicule est à côté de toi mais tu ne le vois pas.",
    geste: "Contrôle-ÉPAULE à gauche AVANT tout changement de direction ou de file. Un simple regard rapide par la vitre latérale.",
    stat: "60% des accidents mortels moto/voiture en agglomération impliquent un changement de file sans contrôle-épaule.",
  },
  {
    id: 'lateral-d',
    label: 'Angle mort latéral DROIT',
    numero: 3,
    polygon: '460,420 590,420 610,600 460,600',
    color: '#dc2626',
    danger: 'Cycliste, piéton qui traverse',
    icon: 'bike',
    iconPos: { x: 535, y: 510 },
    explication: "Symétrique du côté gauche mais SOUVENT plus grand car le rétro droit est plus éloigné du conducteur. Le trottoir et sa zone piétonne y sont partiellement invisibles.",
    geste: "Contrôle-épaule DROIT + regard vers le trottoir avant tout tourne-à-droite, surtout en agglomération.",
    stat: "80% des accidents mortels PL/cycliste se produisent lors d'un tourne-à-droite du camion.",
  },
  {
    id: 'arriere',
    label: 'Angle mort arrière',
    numero: 4,
    polygon: '340,640 460,640 480,760 320,760',
    color: '#ef4444',
    danger: 'Enfant, obstacle bas, autre voiture',
    icon: 'user',
    iconPos: { x: 400, y: 700 },
    explication: "Juste derrière le pare-chocs arrière, sur ~2 m, une zone n'est pas visible par le rétro intérieur (angle) ni par les rétros extérieurs (dépendants de leur réglage).",
    geste: "Contrôle visuel arrière PAR-DESSUS L'ÉPAULE avant toute marche arrière + utilisation caméra/radar si équipé.",
    stat: "Un enfant de 2 ans est masqué derrière une berline standard entre 3 et 15 m de distance selon l'angle de tête.",
  },
  {
    id: 'piliers',
    label: 'Piliers A (montants pare-brise)',
    numero: 5,
    // Deux triangles sur les montants avant
    polygon: '280,215 310,215 340,320 320,320',
    polygonExtra: '460,215 490,215 480,320 460,320',
    color: '#f59e0b',
    danger: 'Piéton en intersection',
    icon: 'user',
    iconPos: { x: 300, y: 265 },
    explication: "Les MONTANTS du pare-brise (piliers A) créent une zone aveugle en biais avant, particulièrement gênante à l'approche des intersections où un piéton peut disparaître derrière le pilier.",
    geste: "MOUVEMENT DE TÊTE en approche d'intersection (comme un lent oui-non-oui) pour voir de part et d'autre du pilier.",
    stat: "En agglomération, 1 accident piéton sur 4 est lié à un pilier A qui a masqué la victime pendant l'approche.",
  },
];

const CONES_VISION = [
  // Cône rétroviseur GAUCHE (extérieur) — vise vers l'arrière-gauche
  { id: 'retro-g', color: '#22d3ee', polygon: '235,405 190,600 60,780 60,405', label: '① Rétro extérieur gauche' },
  // Cône rétroviseur DROIT (extérieur)
  { id: 'retro-d', color: '#22d3ee', polygon: '565,405 610,600 740,780 740,405', label: '② Rétro extérieur droit' },
  // Cône rétroviseur INTÉRIEUR
  { id: 'retro-int', color: '#38bdf8', polygon: '350,285 450,285 620,780 180,780', label: '③ Rétro intérieur' },
];

// Icônes SVG intégrés (piéton/vélo)
function IconPieton({ x, y, size = 26 }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle cx={0} cy={-14} r={5} fill="#111827" />
      <path d={`M-4 -8 L-4 4 L-7 14 M4 -8 L4 4 L7 14 M-6 -4 L6 -4`} stroke="#111827" strokeWidth={3} fill="none" strokeLinecap="round" />
    </g>
  );
}

function IconVelo({ x, y }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle cx={-11} cy={5} r={9} stroke="#111827" strokeWidth={3} fill="none" />
      <circle cx={11} cy={5} r={9} stroke="#111827" strokeWidth={3} fill="none" />
      <path d="M-11 5 L-3 -8 L6 5 L-1 5 L11 5 M-3 -8 L4 -18" stroke="#111827" strokeWidth={3} fill="none" strokeLinecap="round" />
      <circle cx={5} cy={-20} r={4} fill="#111827" />
    </g>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Composant principal
// ══════════════════════════════════════════════════════════════════════════

export default function AnglesMortsSvg() {
  const [selected, setSelected] = useState(null); // zone id
  const [showZones, setShowZones] = useState(true);
  const [showCones, setShowCones] = useState(true);
  const [showShoulder, setShowShoulder] = useState(false);

  const selectedZone = ZONES.find((z) => z.id === selected);

  return (
    <div className="w-full">
      {/* Barre d'outils */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setShowZones((s) => !s)}
          data-testid="angles-morts-toggle-zones"
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-bold transition ${
            showZones ? 'bg-red-600 hover:bg-red-500 text-white' : 'border border-white/20 text-white/70 hover:bg-white/5'
          }`}
        >
          {showZones ? <EyeOff size={13} /> : <Eye size={13} />} Zones aveugles
        </button>
        <button
          type="button"
          onClick={() => setShowCones((s) => !s)}
          data-testid="angles-morts-toggle-cones"
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-bold transition ${
            showCones ? 'bg-cyan-600 hover:bg-cyan-500 text-white' : 'border border-white/20 text-white/70 hover:bg-white/5'
          }`}
        >
          {showCones ? <EyeOff size={13} /> : <Eye size={13} />} Champ des rétros
        </button>
        <button
          type="button"
          onClick={() => setShowShoulder((s) => !s)}
          data-testid="angles-morts-toggle-shoulder"
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12px] font-bold transition ${
            showShoulder ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'border border-white/20 text-white/70 hover:bg-white/5'
          }`}
        >
          {showShoulder ? <EyeOff size={13} /> : <ScanEye size={13} />} Contrôle-épaule
        </button>
        <span className="ml-auto text-white/40 text-[11px] font-mono-spec">Clique une zone pour comprendre</span>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        {/* SVG principal */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#1a2434] to-[#111827]">
          <svg viewBox="0 0 800 800" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Vue de dessus des angles morts autour d'un véhicule">
            <defs>
              <linearGradient id="am-asphalt" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#243044" />
                <stop offset="100%" stopColor="#0f172a" />
              </linearGradient>
              <linearGradient id="am-car-body" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0f172a" />
                <stop offset="45%" stopColor="#1f2937" />
                <stop offset="100%" stopColor="#0b1120" />
              </linearGradient>
              <linearGradient id="am-hood" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#374151" />
                <stop offset="100%" stopColor="#1f2937" />
              </linearGradient>
              <filter id="am-shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="8" stdDeviation="10" floodOpacity="0.5" />
              </filter>
              <filter id="am-glow">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
              <marker id="am-arrow" markerWidth="10" markerHeight="10" refX="8" refY="4" orient="auto">
                <path d="M0,0 L0,8 L10,4 z" fill="#10b981" />
              </marker>
            </defs>

            {/* Fond asphalte */}
            <rect width={800} height={800} fill="url(#am-asphalt)" />
            {/* Lignes de marquage subtiles */}
            <line x1={100} y1={0} x2={100} y2={800} stroke="#334155" strokeWidth={1} strokeDasharray="4 8" opacity={0.4} />
            <line x1={700} y1={0} x2={700} y2={800} stroke="#334155" strokeWidth={1} strokeDasharray="4 8" opacity={0.4} />

            {/* Cônes de vision des rétros */}
            {showCones && CONES_VISION.map((c) => (
              <polygon key={c.id} points={c.polygon} fill={c.color} opacity={0.13} />
            ))}

            {/* ─── VOITURE (vue de dessus, capot vers le haut) ─── */}
            <g filter="url(#am-shadow)">
              {/* Ombre au sol */}
              <ellipse cx={400} cy={640} rx={175} ry={40} fill="#000" opacity={0.35} />
              {/* Carrosserie complète */}
              <rect x={280} y={210} width={240} height={430} rx={55} fill="url(#am-car-body)" stroke="#0b1120" strokeWidth={3} />
              {/* Séparation avant/pare-brise (ligne d'ombre) */}
              <path d="M292 310 Q400 300 508 310" fill="none" stroke="#000" strokeWidth={1.5} opacity={0.5} />
              {/* Capot (avant) */}
              <path d="M290 220 Q400 195 510 220 L510 320 Q400 306 290 320 Z" fill="url(#am-hood)" />
              {/* Pare-brise avant */}
              <path d="M310 320 Q400 306 490 320 L470 415 Q400 410 330 415 Z" fill="#0891b2" opacity={0.35} />
              {/* Toit / habitacle central */}
              <rect x={335} y={415} width={130} height={140} rx={12} fill="#0b1120" opacity={0.65} stroke="#334155" strokeWidth={1} />
              {/* Lunette arrière */}
              <path d="M330 555 Q400 560 470 555 L490 625 Q400 620 310 625 Z" fill="#0891b2" opacity={0.35} />
              {/* Coffre arrière */}
              <path d="M290 625 Q400 615 510 625 L510 640 Q400 630 290 640 Z" fill="url(#am-hood)" />

              {/* Rétros extérieurs */}
              <g>
                <ellipse cx={247} cy={395} rx={14} ry={10} fill="#111827" stroke="#475569" strokeWidth={2} />
                <ellipse cx={247} cy={395} rx={9} ry={6} fill="#22d3ee" opacity={0.7} />
              </g>
              <g>
                <ellipse cx={553} cy={395} rx={14} ry={10} fill="#111827" stroke="#475569" strokeWidth={2} />
                <ellipse cx={553} cy={395} rx={9} ry={6} fill="#22d3ee" opacity={0.7} />
              </g>
              {/* Rétro intérieur */}
              <rect x={385} y={415} width={30} height={8} rx={3} fill="#111827" stroke="#475569" strokeWidth={1} />

              {/* Volant */}
              <circle cx={400} cy={465} r={22} fill="none" stroke="#475569" strokeWidth={2.5} />
              <circle cx={400} cy={465} r={6} fill="#475569" />
              <line x1={400} y1={445} x2={400} y2={485} stroke="#475569" strokeWidth={2} />
              <line x1={380} y1={465} x2={420} y2={465} stroke="#475569" strokeWidth={2} />

              {/* Sièges avant */}
              <rect x={342} y={440} width={40} height={55} rx={10} fill="#1e293b" stroke="#334155" strokeWidth={1} />
              <rect x={418} y={440} width={40} height={55} rx={10} fill="#1e293b" stroke="#334155" strokeWidth={1} />
              {/* Sièges arrière */}
              <rect x={335} y={510} width={130} height={40} rx={8} fill="#1e293b" stroke="#334155" strokeWidth={1} />

              {/* Phares avant */}
              <ellipse cx={315} cy={225} rx={16} ry={8} fill="#fef3c7" opacity={0.85} />
              <ellipse cx={485} cy={225} rx={16} ry={8} fill="#fef3c7" opacity={0.85} />
              {/* Feux arrière */}
              <ellipse cx={310} cy={632} rx={16} ry={7} fill="#dc2626" opacity={0.85} />
              <ellipse cx={490} cy={632} rx={16} ry={7} fill="#dc2626" opacity={0.85} />

              {/* Roues (subtiles, périphérie) */}
              <rect x={272} y={295} width={12} height={40} rx={3} fill="#0b1120" />
              <rect x={516} y={295} width={12} height={40} rx={3} fill="#0b1120" />
              <rect x={272} y={575} width={12} height={40} rx={3} fill="#0b1120" />
              <rect x={516} y={575} width={12} height={40} rx={3} fill="#0b1120" />
            </g>

            {/* Contrôle-épaule (arcs verts autour du conducteur) */}
            {showShoulder && (
              <g opacity={0.7}>
                {/* Arc gauche */}
                <path
                  d="M370 465 Q290 480 240 540"
                  stroke="#10b981"
                  strokeWidth={4}
                  fill="none"
                  markerEnd="url(#am-arrow)"
                  strokeDasharray="8 4"
                />
                <text x={252} y={512} fill="#10b981" fontSize={13} fontWeight="bold">Contrôle G</text>
                {/* Arc droit */}
                <path
                  d="M430 465 Q510 480 560 540"
                  stroke="#10b981"
                  strokeWidth={4}
                  fill="none"
                  markerEnd="url(#am-arrow)"
                  strokeDasharray="8 4"
                />
                <text x={498} y={512} fill="#10b981" fontSize={13} fontWeight="bold">Contrôle D</text>
              </g>
            )}

            {/* Zones aveugles */}
            {showZones && ZONES.map((z) => {
              const isSelected = selected === z.id;
              return (
                <g key={z.id} className="cursor-pointer" onClick={() => setSelected(z.id)} data-testid={`angles-morts-zone-${z.id}`}>
                  <polygon
                    points={z.polygon}
                    fill={z.color}
                    opacity={isSelected ? 0.45 : 0.28}
                    stroke={isSelected ? '#fff' : z.color}
                    strokeWidth={isSelected ? 3 : 1.5}
                    strokeDasharray={isSelected ? '0' : '6 6'}
                  />
                  {z.polygonExtra && (
                    <polygon
                      points={z.polygonExtra}
                      fill={z.color}
                      opacity={isSelected ? 0.45 : 0.28}
                      stroke={isSelected ? '#fff' : z.color}
                      strokeWidth={isSelected ? 3 : 1.5}
                      strokeDasharray={isSelected ? '0' : '6 6'}
                    />
                  )}
                  {/* Numéro */}
                  <circle
                    cx={z.iconPos.x - 30}
                    cy={z.iconPos.y - 30}
                    r={16}
                    fill={isSelected ? '#fff' : '#0b1120'}
                    stroke={z.color}
                    strokeWidth={3}
                  />
                  <text
                    x={z.iconPos.x - 30}
                    y={z.iconPos.y - 26}
                    textAnchor="middle"
                    fontSize={15}
                    fontWeight="bold"
                    fill={isSelected ? z.color : '#fff'}
                  >
                    {z.numero}
                  </text>
                  {/* Icône usager (piéton / vélo) posé dans la zone */}
                  {z.icon === 'user' && <IconPieton x={z.iconPos.x} y={z.iconPos.y} />}
                  {z.icon === 'bike' && <IconVelo x={z.iconPos.x} y={z.iconPos.y} />}
                </g>
              );
            })}

            {/* Titre bas */}
            <g transform="translate(0, 760)">
              <rect width={800} height={40} fill="#000" opacity={0.6} />
              <text x={20} y={26} fill="#fff" fontSize={14} fontWeight="bold">O4 · Angles morts</text>
              <text x={20} y={39} fill="#94a3b8" fontSize={11}>REMC C2b — Adapter sa position sur la chaussée</text>
              <circle cx={770} cy={20} r={16} fill="#dc2626" />
              <text x={770} y={25} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>
        </div>

        {/* Panneau latéral explicatif */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="angles-morts-panel">
          {selectedZone ? (
            <div>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Zone {selectedZone.numero} · Sélectionnée</div>
                  <h4 className="mt-1 font-serif-editorial text-white text-[19px] leading-tight">{selectedZone.label}</h4>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  aria-label="Fermer le panneau"
                  className="h-8 w-8 rounded-full border border-white/15 text-white/60 hover:text-white hover:bg-white/5 inline-flex items-center justify-center"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="mt-4 rounded-lg border border-red-500/40 bg-red-950/25 p-3">
                <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠️ Usager à risque</div>
                <div className="mt-1 text-white/85 text-[13px] font-semibold">{selectedZone.danger}</div>
              </div>

              <div className="mt-3">
                <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Pourquoi c&apos;est un angle mort</div>
                <p className="mt-1 text-white/80 text-[13px] leading-relaxed">{selectedZone.explication}</p>
              </div>

              <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/25 p-3">
                <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Le bon geste</div>
                <p className="mt-1 text-white/90 text-[13px] leading-relaxed">{selectedZone.geste}</p>
              </div>

              <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3">
                <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">📊 Chiffre à retenir</div>
                <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed italic">{selectedZone.stat}</p>
              </div>

              {/* Navigation entre zones */}
              <div className="mt-4 flex items-center justify-between gap-2">
                <button
                  onClick={() => {
                    const idx = ZONES.findIndex((z) => z.id === selected);
                    setSelected(ZONES[(idx - 1 + ZONES.length) % ZONES.length].id);
                  }}
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-full border border-white/15 text-white/70 hover:bg-white/5 text-[12px]"
                  data-testid="angles-morts-prev"
                >
                  <ArrowLeft size={12} /> Précédente
                </button>
                <span className="text-white/40 text-[11px] font-mono-spec">{selectedZone.numero} / {ZONES.length}</span>
                <button
                  onClick={() => {
                    const idx = ZONES.findIndex((z) => z.id === selected);
                    setSelected(ZONES[(idx + 1) % ZONES.length].id);
                  }}
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-full border border-white/15 text-white/70 hover:bg-white/5 text-[12px]"
                  data-testid="angles-morts-next"
                >
                  Suivante <ArrowRight size={12} />
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Guide de lecture</div>
              <h4 className="mt-1 font-serif-editorial text-white text-[19px] leading-tight">5 angles morts à connaître</h4>
              <p className="mt-3 text-white/75 text-[13px] leading-relaxed">
                Autour d&apos;une voiture, il existe <span className="text-red-300 font-semibold">5 zones invisibles</span> depuis le poste de conduite. Un piéton, un cycliste ou même un scooter peut s&apos;y trouver sans qu&apos;aucun rétroviseur ne le montre.
              </p>
              <ul className="mt-4 space-y-2 text-white/80 text-[13px]">
                {ZONES.map((z) => (
                  <li key={z.id}>
                    <button
                      onClick={() => setSelected(z.id)}
                      data-testid={`angles-morts-select-${z.id}`}
                      className="w-full text-left flex items-start gap-2.5 p-2.5 rounded-lg border border-white/10 hover:border-red-500/40 hover:bg-red-950/10 transition"
                    >
                      <span
                        className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-bold text-[11px]"
                        style={{ background: z.color, color: '#fff' }}
                      >
                        {z.numero}
                      </span>
                      <span>
                        <span className="text-white font-semibold">{z.label}</span>
                        <span className="block text-white/50 text-[11.5px]">{z.danger}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-4 rounded-lg border border-cyan-500/30 bg-cyan-950/15 p-3 text-[12px] text-cyan-100 leading-relaxed">
                <Info size={12} className="inline mr-1 text-cyan-300" />
                <span className="text-cyan-200 font-semibold">Astuce prof :</span> active/désactive les 3 toggles en haut pour montrer <span className="whitespace-nowrap">le champ des rétros → puis le contrôle-épaule qui compense.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
